import React from 'react'
import { Box } from '@mui/material'
import type { ReactNode } from 'react'
import type { ResponsiveStyleValue } from '@mui/system'
import { WaveTabBar } from './WaveTabBar'

// =============================================================================
// TypeScript Interfaces for Compound Components
// =============================================================================

/**
 * Props for the main WaveSidebar component
 * Handles layout, tab selection, and collapse state only
 */
export interface WaveSidebarProps {
  /** Currently active tab ID */
  activeTab?: string
  
  /** Whether the sidebar is collapsed (only tab bar visible) */
  collapsed?: boolean
  
  /** Fixed width for the sidebar (supports responsive values) */
  width?: ResponsiveStyleValue<number | string>
  
  /** Callback when tab changes */
  onTabChange?: (tabId: string) => void
  
  /** Callback when collapsed state changes */
  onCollapsedChange?: (collapsed: boolean) => void
  
  /** Optional className for styling */
  className?: string
  
  /** WaveSidebar.Tab children components */
  children: ReactNode
}

/**
 * Props for WaveSidebar.Tab compound component
 * Provides tab metadata and wraps content
 */
export interface WaveSidebarTabProps {
  /** Unique tab identifier */
  id: string
  
  /** Icon to display in tab bar */
  icon: ReactNode
  
  /** Label text for tab */
  label: string
  
  /** Whether this tab is disabled */
  disabled?: boolean
  
  /** Tab content (e.g., FileTab, DataTab, etc.) */
  children: ReactNode
}

// =============================================================================
// Helper Functions for Compound Component Logic
// =============================================================================

/**
 * Find the active child component based on activeTab
 * Recursively handles React Fragments like WaveTabBar does
 */
const findActiveChild = (children: ReactNode, activeTab: string): ReactNode => {
  let activeChild: ReactNode = null
  
  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child)) {
      // Handle React Fragments recursively (same logic as WaveTabBar)
      if (child.type === React.Fragment) {
        activeChild = findActiveChild((child.props as any).children, activeTab)
        if (activeChild) return // Stop searching once found
      } else if (React.isValidElement<WaveSidebarTabProps>(child) && child.props.id === activeTab) {
        // Direct WaveSidebar.Tab component
        activeChild = child.props.children
      }
    }
  })
  
  return activeChild
}

// =============================================================================
// WaveSidebar.Tab Compound Component
// =============================================================================

/**
 * Tab container component for WaveSidebar compound pattern
 * Just returns children - parent WaveSidebar handles rendering logic
 */
export const WaveSidebarTab = ({ children }: WaveSidebarTabProps) => {
  return <>{children}</>
}

// =============================================================================
// Main WaveSidebar Component
// =============================================================================

/**
 * WaveSidebar compound component with clean composition API
 * Handles layout, tab switching, and collapse state only
 */
export const WaveSidebar = ({ 
  activeTab = 'files',
  collapsed = false,
  width = 350,
  onTabChange,
  onCollapsedChange,
  className,
  children 
}: WaveSidebarProps) => {
  // Find the active tab content
  const activeChild = findActiveChild(children, activeTab)
  
  return (
    <Box 
      className={className} 
      sx={{ 
        display: 'flex',
        height: '100%',
        width: collapsed ? 60 : width,
        flexShrink: 0
      }}
    >
      {/* Tab Bar - now extracts tabs from children internally */}
      <WaveTabBar
        activeTab={activeTab}
        collapsed={collapsed}
        onTabChange={onTabChange || (() => {})}
        onCollapsedChange={onCollapsedChange}
      >
        {children}
      </WaveTabBar>
      
      {/* Content Area */}
      {!collapsed && (
        <Box 
          sx={{ 
            width: 'calc(100% - 60px)', // Full width minus tab bar width
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'background.paper',
            overflow: 'hidden'
          }}
        >
          {activeChild}
        </Box>
      )}
    </Box>
  )
}

// Attach compound component for clean API
WaveSidebar.Tab = WaveSidebarTab 